import { Plus, Receipt } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtMoney } from '../shared/format'
import { ExpenseDetailModal } from './components/expenses/ExpenseDetailModal'
import { ExpenseFormModal } from './components/expenses/ExpenseFormModal'
import { EXPENSE_PERMS as P, EXPENSE_STATUS_TONE, useCustomerExpenses, type CustomerExpense } from './expenses'
import { useCrm } from './ui'

type View = 'mine' | 'approval' | 'all' | 'legacy'

/** Müşteri giderleri: kendi giderlerim, onay bekleyenler (firma sahibi), tümü ve muhasebeye aktarılmamış önceki kayıtlar. */
export function CustomerExpensesPage() {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const [params, setParams] = useSearchParams()
  const approver = can(P.approve)
  const [view, setView] = useState<View>(approver ? 'approval' : 'mine')
  const [creating, setCreating] = useState(false)
  const open = params.get('id')
  const q = useCustomerExpenses({ userId: view === 'mine' ? (user?.id ?? '') : '' })
  const rows = (q.data ?? []).filter((e) =>
    view === 'approval' ? e.status === 'pending' || e.status === 'info_requested' : view === 'legacy' ? e.legacy && e.status === 'approved' && !e.financeRef : true,
  )
  const total = rows.reduce((n, e) => n + Number(e.amount), 0)

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.expenses.pageTitle')}
        description={t('crm.expenses.pageSubtitle')}
        actions={
          can(P.create) ? (
            <Button onClick={() => setCreating(true)}>
              <Plus />
              {t('crm.expenses.new')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-3">
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList size="sm">
            <TabsTrigger value="mine">{t('crm.expenses.views.mine')}</TabsTrigger>
            {approver ? <TabsTrigger value="approval">{t('crm.expenses.views.approval')}</TabsTrigger> : null}
            {approver || can(P.readAll) ? <TabsTrigger value="all">{t('crm.expenses.views.all')}</TabsTrigger> : null}
            {can(P.postLegacy) ? <TabsTrigger value="legacy">{t('crm.expenses.views.legacy')}</TabsTrigger> : null}
          </TabsList>
        </Tabs>
        <section className="rounded-xl border bg-card shadow-card">
          {q.isError ? (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} />
          ) : q.isLoading ? (
            <Skeleton className="m-4 h-48" />
          ) : !rows.length ? (
            <EmptyState icon={Receipt} title={t(view === 'approval' ? 'crm.expenses.noPending' : view === 'legacy' ? 'crm.expenses.noLegacy' : 'crm.expenses.empty')} />
          ) : (
            <ExpenseTable rows={rows} total={total} onOpen={(id) => setParams({ id })} />
          )}
        </section>
      </div>
      {creating ? <ExpenseFormModal onClose={() => setCreating(false)} /> : null}
      {open ? <ExpenseDetailModal id={open} onClose={() => setParams({})} /> : null}
    </>
  )
}

export function ExpenseTable({ rows, total, onOpen, hideCustomer }: { rows: CustomerExpense[]; total?: number; onOpen: (id: string) => void; hideCustomer?: boolean }) {
  const { t } = useTranslation()
  const { staffName, customerName } = useCrm()
  return (
    <div className="overflow-x-auto">
      <Table>
        <THead>
          <TR>
            <TH>{t('crm.expenses.spentOn')}</TH>
            {hideCustomer ? null : <TH>{t('crm.expenses.customer')}</TH>}
            <TH>{t('crm.expenses.title')}</TH>
            <TH>{t('crm.expenses.spender')}</TH>
            <TH>{t('crm.expenses.paymentMethod')}</TH>
            <TH>{t('crm.expenses.status')}</TH>
            <TH className="text-right">{t('crm.expenses.amount')}</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((e) => (
            <TR key={e.id} className="cursor-pointer" onClick={() => onOpen(e.id)}>
              <TD className="whitespace-nowrap tabular-nums">{fmtDate(e.spentOn)}</TD>
              {hideCustomer ? null : <TD>{customerName(e.accountId)}</TD>}
              <TD>
                <span className="font-medium">{e.title}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">{t(`crm.expenses.categories.${e.category}`)}</span>
              </TD>
              <TD>{staffName(e.userId)}</TD>
              <TD className="text-xs">{t(`crm.expenses.methods.${e.paymentMethod}`)}</TD>
              <TD>
                <Badge tone={EXPENSE_STATUS_TONE[e.status]}>{t(`crm.expenses.statuses.${e.status}`)}</Badge>
              </TD>
              <TD className="text-right font-medium tabular-nums">{fmtMoney(e.amount)}</TD>
            </TR>
          ))}
          {total !== undefined ? (
            <TR className="border-t-2 font-semibold">
              <TD colSpan={hideCustomer ? 5 : 6}>{t('crm.expenses.total')}</TD>
              <TD className="text-right tabular-nums">{fmtMoney(total.toFixed(2))}</TD>
            </TR>
          ) : null}
        </TBody>
      </Table>
    </div>
  )
}
